import React, { createContext, useContext, useMemo } from 'react'
import { GuildPermissions } from '@/services/GuildUserService/GuildUserService'
import { SerializedSkipCategoryPreference } from '@/services/ParentalControlsService'
import { ContentWithWatchableReasons } from '@/services/ProjectPhaseService'
import { TicketsStatusReference } from '@/types/codegen-federation'
import { Downloadable } from '../DownloadablesService'
import { PlayableLivestream } from '../LivestreamService'
import { Note } from '../StoriesService/types'
import { CurrentLivestream, VideoMetaData, WatchableNode } from './types'

interface WatchProjectContextValue {
  bonusVideos: VideoMetaData[]
  livestreams: PlayableLivestream[]
  downloadables: Downloadable[]
  communityStories: Note[]
  watchables: WatchableNode[]
  hasLivestreams: boolean
  hasDownloadables: boolean
  hasMerchItems: boolean
  hasBonusVideos: boolean
  currentLivestream: CurrentLivestream | null
  ticketStatus: TicketsStatusReference | null
  watchableContent: ContentWithWatchableReasons | null
  guildPermissions: GuildPermissions | null
  isGuildMember: boolean
  parentalControlsApplied: number
  parentalControlsTotal: number
  skipCategoryPreferences: SerializedSkipCategoryPreference[]
}

interface WatchProjectProviderProps {
  children: React.ReactNode
  bonusVideos: VideoMetaData[]
  livestreams: PlayableLivestream[]
  downloadables: Downloadable[]
  communityStories: Note[]
  watchables: WatchableNode[]
  hasLivestreams: boolean
  hasDownloadables: boolean
  hasMerchItems: boolean
  hasBonusVideos: boolean
  currentLivestream: CurrentLivestream | null
  ticketStatus: TicketsStatusReference | null
  watchableContent?: ContentWithWatchableReasons | null
  guildPermissions?: GuildPermissions | null
  isGuildMember?: boolean
  parentalControlsApplied: number
  parentalControlsTotal: number
  skipCategoryPreferences: SerializedSkipCategoryPreference[]
}

const WatchProjectContext = createContext<WatchProjectContextValue | undefined>(undefined)

export const WatchProjectProvider: React.FC<WatchProjectProviderProps> = ({
  children,
  bonusVideos,
  livestreams,
  downloadables,
  communityStories,
  watchables,
  hasLivestreams,
  hasDownloadables,
  hasMerchItems,
  hasBonusVideos,
  currentLivestream,
  ticketStatus,
  watchableContent = null,
  guildPermissions = null,
  isGuildMember = false,
  parentalControlsApplied,
  parentalControlsTotal,
  skipCategoryPreferences,
}) => {
  const value = useMemo(
    () => ({
      bonusVideos,
      livestreams,
      downloadables,
      communityStories,
      watchables,
      hasLivestreams,
      hasDownloadables,
      hasMerchItems,
      hasBonusVideos,
      currentLivestream,
      ticketStatus,
      watchableContent,
      guildPermissions,
      isGuildMember,
      parentalControlsApplied,
      parentalControlsTotal,
      skipCategoryPreferences,
    }),
    [
      bonusVideos,
      livestreams,
      downloadables,
      communityStories,
      watchables,
      hasLivestreams,
      hasDownloadables,
      hasMerchItems,
      hasBonusVideos,
      currentLivestream,
      ticketStatus,
      watchableContent,
      guildPermissions,
      isGuildMember,
      parentalControlsApplied,
      parentalControlsTotal,
      skipCategoryPreferences,
    ],
  )

  return <WatchProjectContext.Provider value={value}>{children}</WatchProjectContext.Provider>
}

export const useWatchProjectContext = () => {
  const context = useContext(WatchProjectContext)
  if (!context) {
    throw new Error('useWatchProjectContext must be used within a WatchProjectProvider')
  }
  return context
}
